import { z } from "zod";

// Create blog post schema
export const createBlogPostSchema = z.object({
  title: z
    .string()
    .min(3, { message: "Title must be at least 3 characters." })
    .max(200, { message: "Title must not exceed 200 characters." }),
  excerpt: z
    .string()
    .min(10, { message: "Excerpt must be at least 10 characters." })
    .max(500, { message: "Excerpt must not exceed 500 characters." })
    .optional(),
  content: z
    .string()
    .min(50, { message: "Content must be at least 50 characters." }),
  coverImage: z
    .string()
    .url({ message: "Cover image must be a valid URL." })
    .optional(),
  published: z.boolean().default(false),
  tagNames: z.array(z.string()).default([]),
  categoryId: z.string().optional(),
});

// Update blog post schema
export const updateBlogPostSchema = createBlogPostSchema.partial().extend({
  id: z.string().min(1, { message: "Post ID is required." }),
});

// Create comment schema
export const createCommentSchema = z.object({
  content: z
    .string()
    .min(5, { message: "Comment must be at least 5 characters." })
    .max(1000, { message: "Comment must not exceed 1000 characters." }),
  postSlug: z
    .string()
    .min(1, { message: "Post slug is required." }),
  parentId: z.string().optional(),
});

// Like/reaction schema
export const likeSchema = z.object({
  postSlug: z
    .string()
    .min(1, { message: "Post slug is required." }),
  reaction: z
    .enum(["like", "love", "helpful", "insightful"])
    .default("like"),
});

// Share schema
export const shareSchema = z.object({
  postSlug: z
    .string()
    .min(1, { message: "Post slug is required." }),
  platform: z
    .enum(["twitter", "facebook", "linkedin", "whatsapp", "email", "copy"])
    .default("copy"),
});

// Blog search filters schema
export const blogFiltersSchema = z.object({
  search: z.string().optional(),
  tags: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  author: z.string().optional(),
  sortBy: z
    .enum(["recent", "popular", "mostViewed", "mostLiked"])
    .default("recent"),
  page: z.number().min(1).default(1),
  limit: z.number().min(1).max(100).default(12),
});

// Export TypeScript types
export type CreateBlogPostValues = z.infer<typeof createBlogPostSchema>;
export type UpdateBlogPostValues = z.infer<typeof updateBlogPostSchema>;
export type CreateCommentValues = z.infer<typeof createCommentSchema>;
export type LikeValues = z.infer<typeof likeSchema>;
export type ShareValues = z.infer<typeof shareSchema>;
export type BlogFiltersValues = z.infer<typeof blogFiltersSchema>;

// Export all blog schemas as a collection
export const blogSchemas = {
  createPost: createBlogPostSchema,
  updatePost: updateBlogPostSchema,
  createComment: createCommentSchema,
  like: likeSchema,
  share: shareSchema,
  filters: blogFiltersSchema,
} as const;
